import React from 'react';
import { LucideIcon, TrendingUp, TrendingDown, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';

export interface ExecutiveMetricCardProps {
  title: string;
  value: string | number;
  isCurrency?: boolean;
  subtitle?: string;
  infoTooltip?: string;
  icon?: LucideIcon;
  iconColorClass?: string;
  iconBgClass?: string;
  trend?: {
    value: number; // Porcentagem de variação
    isPositive?: boolean;
    invertColor?: boolean; // Se positivo for ruim (ex: CAC subindo)
    label?: string;
  };
  badge?: {
    text: string;
    variant?: 'default' | 'success' | 'warning' | 'info' | 'purple';
  };
  loading?: boolean;
  className?: string;
}

export const formatBrl = (val: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(val);
};

export const ExecutiveMetricCard: React.FC<ExecutiveMetricCardProps> = ({
  title,
  value,
  isCurrency = false,
  subtitle,
  infoTooltip,
  icon: Icon,
  iconColorClass = 'text-primary',
  iconBgClass = 'bg-primary/10',
  trend,
  badge,
  loading = false,
  className = '',
}) => {
  if (loading) {
    return (
      <div className={`p-5 bg-card rounded-xl border border-border shadow-xs animate-pulse ${className}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="h-4 w-28 bg-muted rounded"></div>
          <div className="h-9 w-9 bg-muted rounded-lg"></div>
        </div>
        <div className="h-7 w-36 bg-muted rounded mb-2"></div>
        <div className="h-3 w-48 bg-muted rounded"></div>
      </div>
    );
  }

  const formattedValue = typeof value === 'number' && isCurrency ? formatBrl(value) : value;

  const badgeVariantClasses = {
    default: 'bg-muted text-muted-foreground',
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800',
    warning: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800',
    info: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-800',
    purple: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-400 dark:border-purple-800',
  };

  const isTrendGood = trend ? (trend.invertColor ? !trend.isPositive : trend.isPositive) : true;

  return (
    <div className={`p-5 bg-card rounded-xl border border-border shadow-xs hover:shadow-md transition-shadow relative overflow-hidden flex flex-col justify-between ${className}`}>
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider truncate">
              {title}
            </span>
            {infoTooltip && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground/60 hover:text-primary transition-colors p-0.5 rounded-full hover:bg-muted cursor-pointer shrink-0"
                      aria-label={`Explicação sobre ${title}`}
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    align="start"
                    className="max-w-[280px] p-3 text-xs bg-slate-900 text-slate-100 dark:bg-slate-100 dark:text-slate-900 rounded-xl shadow-xl border border-white/10 dark:border-slate-800 leading-relaxed font-normal"
                  >
                    {infoTooltip}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {Icon && (
            <div className={`p-2 rounded-lg ${iconBgClass} ${iconColorClass} shrink-0`}>
              <Icon className="h-5 w-5" />
            </div>
          )}
        </div>

        <div className="flex items-baseline gap-2 mb-1 flex-wrap">
          <h3 className="text-2xl font-bold tracking-tight text-foreground">
            {formattedValue}
          </h3>

          {badge && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium border ${
                badgeVariantClasses[badge.variant || 'default']
              }`}
            >
              {badge.text}
            </span>
          )}
        </div>
      </div>

      <div className="mt-3 pt-2 border-t border-border/50 flex items-center justify-between text-xs text-muted-foreground">
        {trend ? (
          <div className="flex items-center gap-1 font-medium">
            <span
              className={`inline-flex items-center gap-0.5 ${
                isTrendGood ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
              }`}
            >
              {trend.isPositive ? (
                <TrendingUp className="h-3.5 w-3.5" />
              ) : (
                <TrendingDown className="h-3.5 w-3.5" />
              )}
              {trend.value > 0 ? `+${trend.value}%` : `${trend.value}%`}
            </span>
            {trend.label && <span className="text-muted-foreground ml-1">{trend.label}</span>}
          </div>
        ) : subtitle ? (
          <span className="truncate">{subtitle}</span>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        )}
      </div>
    </div>
  );
};
